import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  LightBulbIcon,
  CheckCircleIcon,
  XMarkIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const FeedbackWidget = () => {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());

  useEffect(() => {
    fetchFeedback();
  }, []);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const feedbackCollection = collection(db, 'feedback');
      const feedbackQuery = query(feedbackCollection, orderBy('createdAt', 'desc'));
      const feedbackSnapshot = await getDocs(feedbackQuery);
      
      const feedbackData = feedbackSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date()
      }));
      
      setFeedback(feedbackData);
    } catch (error) {
      console.error('Error fetching feedback:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateFeedbackStatus = async (feedbackId, status) => {
    try {
      const feedbackRef = doc(db, 'feedback', feedbackId);
      await updateDoc(feedbackRef, { 
        status,
        updatedAt: new Date()
      });
      
      setFeedback(prevFeedback => 
        prevFeedback.map(item => 
          item.id === feedbackId ? { ...item, status } : item
        )
      );
    } catch (error) {
      console.error('Error updating feedback status:', error);
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback? This action cannot be undone.')) {
      return;
    }
    
    try {
      await deleteDoc(doc(db, 'feedback', feedbackId));
      setFeedback(prevFeedback => prevFeedback.filter(item => item.id !== feedbackId));
      if (selectedFeedback?.id === feedbackId) {
        setSelectedFeedback(null);
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
    }
  };

  const toggleExpanded = (feedbackId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(feedbackId)) {
      newExpanded.delete(feedbackId);
    } else {
      newExpanded.add(feedbackId);
    }
    setExpandedItems(newExpanded);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'game_idea':
        return <LightBulbIcon className="h-5 w-5" style={{color: '#F39C12'}} />;
      case 'feedback':
        return <ChatBubbleLeftRightIcon className="h-5 w-5" style={{color: '#3498DB'}} />;
      case 'issue':
        return <ExclamationTriangleIcon className="h-5 w-5" style={{color: '#E74C3C'}} />;
      default:
        return <ChatBubbleLeftRightIcon className="h-5 w-5" style={{color: '#95A5A6'}} />;
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'game_idea': return 'GAME IDEA';
      case 'feedback': return 'FEEDBACK';
      case 'issue': return 'ISSUE';
      default: return type?.toUpperCase() || 'UNKNOWN';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return '#E74C3C';
      case 'in_progress': return '#F39C12';
      case 'resolved': return '#27AE60';
      case 'dismissed': return '#95A5A6';
      default: return '#95A5A6';
    }
  };

  const filteredFeedback = feedback.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'type') return true; // This will be handled by type-specific filters
    return item.status === filter || item.type === filter;
  });

  const getStats = () => {
    const total = feedback.length;
    const newCount = feedback.filter(f => f.status === 'new').length;
    const gameIdeas = feedback.filter(f => f.type === 'game_idea').length;
    const issues = feedback.filter(f => f.type === 'issue').length;
    const feedbackCount = feedback.filter(f => f.type === 'feedback').length;
    
    return { total, newCount, gameIdeas, issues, feedbackCount };
  };

  const stats = getStats();

  if (loading) {
    return (
      <div className="studio-card rounded-xl p-6" style={{
        background: 'rgba(52, 73, 94, 0.8)',
        border: 'none',
        backdropFilter: 'blur(10px)'
      }}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{borderColor: '#F1C40F'}}></div>
          <span className="ml-3 font-bold" style={{color: '#F1C40F', fontFamily: '"Share Tech Mono", monospace'}}>
            Loading feedback...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="studio-card rounded-xl p-6" style={{
      background: 'rgba(52, 73, 94, 0.8)',
      border: 'none',
      backdropFilter: 'blur(10px)'
    }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-6 w-6" style={{color: '#F1C40F'}} />
          <h2 className="text-xl font-black tracking-wider" style={{
            color: '#F1C40F',
            textShadow: '1px 1px 0px #E67E22',
            fontFamily: '"Share Tech Mono", monospace'
          }}>
            💬 USER FEEDBACK
          </h2>
        </div>
        <button
          onClick={fetchFeedback}
          className="px-3 py-1 rounded-lg text-sm font-bold transition-all duration-100"
          style={{
            background: 'rgba(52, 152, 219, 0.2)',
            border: '1px solid rgba(52, 152, 219, 0.4)',
            color: '#3498DB'
          }}
        >
          🔄 REFRESH
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
        <div className="p-3 rounded-lg" style={{background: 'rgba(52, 73, 94, 0.6)'}}>
          <div className="text-xs font-bold" style={{color: '#BDC3C7', fontFamily: '"Share Tech Mono", monospace'}}>TOTAL</div>
          <div className="text-lg font-black" style={{color: '#F1C40F', fontFamily: '"Share Tech Mono", monospace'}}>{stats.total}</div>
        </div>
        <div className="p-3 rounded-lg" style={{background: 'rgba(231, 76, 60, 0.2)'}}>
          <div className="text-xs font-bold" style={{color: '#BDC3C7', fontFamily: '"Share Tech Mono", monospace'}}>NEW</div>
          <div className="text-lg font-black" style={{color: '#E74C3C', fontFamily: '"Share Tech Mono", monospace'}}>{stats.newCount}</div>
        </div>
        <div className="p-3 rounded-lg" style={{background: 'rgba(243, 156, 18, 0.2)'}}>
          <div className="text-xs font-bold" style={{color: '#BDC3C7', fontFamily: '"Share Tech Mono", monospace'}}>IDEAS</div>
          <div className="text-lg font-black" style={{color: '#F39C12', fontFamily: '"Share Tech Mono", monospace'}}>{stats.gameIdeas}</div>
        </div>
        <div className="p-3 rounded-lg" style={{background: 'rgba(52, 152, 219, 0.2)'}}>
          <div className="text-xs font-bold" style={{color: '#BDC3C7', fontFamily: '"Share Tech Mono", monospace'}}>FEEDBACK</div>
          <div className="text-lg font-black" style={{color: '#3498DB', fontFamily: '"Share Tech Mono", monospace'}}>{stats.feedbackCount}</div>
        </div>
        <div className="p-3 rounded-lg" style={{background: 'rgba(231, 76, 60, 0.2)'}}>
          <div className="text-xs font-bold" style={{color: '#BDC3C7', fontFamily: '"Share Tech Mono", monospace'}}>ISSUES</div>
          <div className="text-lg font-black" style={{color: '#E74C3C', fontFamily: '"Share Tech Mono", monospace'}}>{stats.issues}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        {['all', 'new', 'game_idea', 'feedback', 'issue', 'resolved'].map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            className="px-3 py-1 rounded-lg text-xs font-bold transition-all duration-100"
            style={{
              background: filter === filterType ? 'rgba(241, 196, 15, 0.3)' : 'rgba(52, 73, 94, 0.6)',
              border: `1px solid ${filter === filterType ? '#F1C40F' : 'rgba(149, 165, 166, 0.3)'}`,
              color: filter === filterType ? '#F1C40F' : '#BDC3C7',
              fontFamily: '"Share Tech Mono", monospace'
            }}
          >
            {filterType.replace('_', ' ').toUpperCase()}
          </button>
        ))}
      </div>

      {/* Feedback List */}
      <div className="space-y-3 max-h-96 overflow-y-auto">
        <AnimatePresence>
          {filteredFeedback.map((item) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="p-4 rounded-lg border transition-all duration-200"
              style={{
                background: 'rgba(52, 73, 94, 0.4)',
                border: `1px solid ${getStatusColor(item.status)}20`
              }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {getTypeIcon(item.type)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold px-2 py-1 rounded" style={{
                        background: `${getStatusColor(item.status)}20`,
                        color: getStatusColor(item.status),
                        fontFamily: '"Share Tech Mono", monospace'
                      }}>
                        {getTypeLabel(item.type)}
                      </span>
                      <span className="text-xs font-bold px-2 py-1 rounded" style={{
                        background: `${getStatusColor(item.status)}20`,
                        color: getStatusColor(item.status),
                        fontFamily: '"Share Tech Mono", monospace'
                      }}>
                        {item.status?.toUpperCase() || 'NEW'}
                      </span>
                    </div>
                    
                    <div className="text-sm font-bold mb-1" style={{color: '#ECF0F1', fontFamily: '"Share Tech Mono", monospace'}}>
                      From: {item.userEmail}
                    </div>
                    
                    <div className="text-xs mb-2" style={{color: '#BDC3C7', fontFamily: '"Share Tech Mono", monospace'}}>
                      {item.createdAt.toLocaleDateString()} {item.createdAt.toLocaleTimeString()}
                    </div>
                    
                    <div className="text-sm" style={{color: '#ECF0F1', fontFamily: '"Share Tech Mono", monospace'}}>
                      {expandedItems.has(item.id) ? (
                        item.message
                      ) : (
                        `${item.message.substring(0, 100)}${item.message.length > 100 ? '...' : ''}`
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-1 ml-3">
                  <button
                    onClick={() => toggleExpanded(item.id)}
                    className="p-1 rounded transition-all duration-100"
                    style={{
                      background: 'rgba(52, 152, 219, 0.2)',
                      color: '#3498DB'
                    }}
                    title={expandedItems.has(item.id) ? 'Collapse' : 'Expand'}
                  >
                    {expandedItems.has(item.id) ? 
                      <EyeSlashIcon className="h-4 w-4" /> : 
                      <EyeIcon className="h-4 w-4" />
                    }
                  </button>
                  
                  {item.status !== 'resolved' && (
                    <button
                      onClick={() => updateFeedbackStatus(item.id, 'resolved')}
                      className="p-1 rounded transition-all duration-100"
                      style={{
                        background: 'rgba(39, 174, 96, 0.2)',
                        color: '#27AE60'
                      }}
                      title="Mark as Resolved"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => deleteFeedback(item.id)}
                    className="p-1 rounded transition-all duration-100"
                    style={{
                      background: 'rgba(231, 76, 60, 0.2)',
                      color: '#E74C3C'
                    }}
                    title="Delete"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        
        {filteredFeedback.length === 0 && (
          <div className="text-center py-8">
            <div className="text-lg font-bold mb-2" style={{color: '#BDC3C7', fontFamily: '"Share Tech Mono", monospace'}}>
              No feedback found
            </div>
            <div className="text-sm" style={{color: '#95A5A6', fontFamily: '"Share Tech Mono", monospace'}}>
              {filter !== 'all' ? 'Try changing the filter or' : ''} Check back later for user submissions
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackWidget; 